import {
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Switch,
  Table,
  TimePicker,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { academicsApi, type PeriodSlot } from '@/api/academics';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface FormShape {
  name: string;
  range: [Dayjs, Dayjs];
  sortOrder: number;
}

export function PeriodSlotsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('ACADEMICS:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('ACADEMICS:EDIT'));

  const [editing, setEditing] = useState<PeriodSlot | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['academics', 'period-slots'],
    queryFn: () => academicsApi.listPeriodSlots(false),
  });

  const upsert = useMutation({
    mutationFn: (v: FormShape) => {
      const body = {
        name: v.name.trim(),
        startTime: v.range[0].format('HH:mm:ss'),
        endTime: v.range[1].format('HH:mm:ss'),
        sortOrder: v.sortOrder,
      };
      return editing ? academicsApi.updatePeriodSlot(editing.id, body) : academicsApi.createPeriodSlot(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Period slot updated' : 'Period slot created');
      qc.invalidateQueries({ queryKey: ['academics', 'period-slots'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) =>
      academicsApi.togglePeriodSlot(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['academics', 'period-slots'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    const max = (data ?? []).reduce((m, p) => Math.max(m, p.sortOrder), 0);
    form.setFieldsValue({ sortOrder: max + 1 });
  };
  const openEdit = (row: PeriodSlot) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      range: [dayjs(row.startTime, 'HH:mm:ss'), dayjs(row.endTime, 'HH:mm:ss')],
      sortOrder: row.sortOrder,
    });
  };
  const open = creating || editing != null;

  const columns: TableProps<PeriodSlot>['columns'] = [
    { title: '#', dataIndex: 'sortOrder', width: 60, align: 'right' },
    {
      title: 'Slot',
      dataIndex: 'name',
      render: (n: string) => <Typography.Text strong>{n}</Typography.Text>,
    },
    {
      title: 'Time',
      width: 200,
      render: (_, row) =>
        `${dayjs(row.startTime, 'HH:mm:ss').format('HH:mm')} – ${dayjs(row.endTime, 'HH:mm:ss').format('HH:mm')}`,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Academics"
        title="Period slots"
        description="Time bands used by the timetable. Sort order controls the row order."
        count={{ value: data?.length ?? 0, label: 'slots' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New slot
            </Button>
          ) : null
        }
      />

      <Table rowKey="id" size="middle" loading={isLoading} columns={columns} dataSource={data ?? []} pagination={false} />

      <Modal
        title={editing ? `Edit · ${editing.name}` : 'New period slot'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 50 }]}>
              <Input autoFocus placeholder="e.g. Period 1, Break, Lunch" />
            </Form.Item>
            <Form.Item label="Start – end" name="range" rules={[{ required: true }]}>
              <TimePicker.RangePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Sort order" name="sortOrder" rules={[{ required: true, type: 'number', min: 1 }]}>
              <InputNumber style={{ width: '100%' }} min={1} step={1} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
